import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Avatar,
  Slide,
  Fade,
  useTheme
} from '@mui/material';
import {
  Close as CloseIcon,
  Message as MessageIcon,
  Group as GroupIcon
} from '@mui/icons-material';

export interface NotificationCardData {
  id: string;
  title: string;
  body: string;
  groupId?: string;
  groupName?: string;
  senderName?: string;
  timestamp: Date;
  avatar?: string;
}

interface NotificationCardProps {
  notification: NotificationCardData;
  onClose: (id: string) => void;
  onClick?: (notification: NotificationCardData) => void;
}

export const NotificationCard: React.FC<NotificationCardProps> = ({
  notification,
  onClose,
  onClick
}) => {
  const theme = useTheme();
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setShow(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setShow(false);
    setTimeout(() => onClose(notification.id), 200);
  };

  const handleClick = () => {
    if (onClick) {
      onClick(notification);
    }
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    return date.toLocaleDateString();
  };

  return (
    <Slide direction="left" in={show} timeout={300}>
      <Card
        sx={{
          width: 360,
          mb: 2,
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.2s ease-in-out',
          boxShadow: theme.shadows[8],
          border: `1px solid ${theme.palette.divider}`,
          '&:hover': onClick ? {
            transform: 'translateX(-4px)',
            boxShadow: theme.shadows[12],
          } : {},
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
        }}
        onClick={handleClick}
      >
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
            <Avatar
              sx={{ 
                width: 40, 
                height: 40, 
                mr: 1.5,
                bgcolor: theme.palette.primary.main,
                fontSize: '1rem'
              }}
            >
              {notification.avatar ? (
                <img src={notification.avatar} alt="" style={{ width: '100%', height: '100%' }} />
              ) : (
                notification.senderName?.[0]?.toUpperCase() || <MessageIcon />
              )}
            </Avatar>
            
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mr: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1
                  }}
                >
                  {notification.senderName || 'New Message'}
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {formatTime(notification.timestamp)}
                </Typography>
              </Box>
              
              {notification.groupName && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <GroupIcon sx={{ fontSize: 14, mr: 0.5, color: 'text.secondary' }} />
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {notification.groupName}
                  </Typography>
                </Box>
              )}
            </Box>

            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
              sx={{ 
                ml: 1,
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                  color: 'text.primary'
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Message Body */}
          <Typography 
            variant="body2" 
            color="text.primary"
            sx={{ 
              mt: 1,
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {notification.body}
          </Typography>

          {/* Action hint */}
          {onClick && (
            <Typography 
              variant="caption" 
              color="primary"
              sx={{ 
                mt: 1,
                display: 'block',
                fontWeight: 500,
                opacity: 0.8
              }}
            >
              Click to view message
            </Typography>
          )}
        </CardContent>
      </Card>
    </Slide>
  );
};

interface NotificationContainerProps {
  notifications: NotificationCardData[];
  onCloseNotification: (id: string) => void;
  onClickNotification?: (notification: NotificationCardData) => void;
}

export const NotificationContainer: React.FC<NotificationContainerProps> = ({
  notifications,
  onCloseNotification,
  onClickNotification
}) => {
  if (notifications.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 100,
        right: 20,
        zIndex: 2000,
        pointerEvents: 'none',
        maxHeight: 'calc(100vh - 120px)',
        overflow: 'hidden',
        '& > *': {
          pointerEvents: 'auto'
        }
      }}
    >
      <Fade in timeout={200}>
        <Box>
          {notifications.slice(0, 4).map((notification) => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onClose={onCloseNotification}
              onClick={onClickNotification}
            />
          ))}
          
          {notifications.length > 4 && (
            <Card sx={{ width: 360, textAlign: 'center', p: 1 }}>
              <Typography variant="caption" color="text.secondary">
                +{notifications.length - 4} more notifications
              </Typography>
            </Card>
          )}
        </Box>
      </Fade>
    </Box>
  );
}; 